import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Filter, Plus, Edit, Trash2, Eye, ToggleLeft, ToggleRight,
  Calendar, Percent, DollarSign, Loader, X, AlertCircle, CheckCircle,
  RefreshCw, Tag, Store, Package, TrendingUp, Users, Clock
} from 'lucide-react';
import adminService, { 
  Offer, OfferListResponse, CreateOfferRequest, UpdateOfferRequest,
  GetOffersParams, OfferType, OfferStatus, Category, Business
} from '../../services/admin/adminService';
import toast from 'react-hot-toast';

const OffersManagement: React.FC = () => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState<Offer[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<OfferStatus>('all');
  const [typeFilter, setTypeFilter] = useState<OfferType | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOffers, setTotalOffers] = useState(0);
  
  // Modals
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
  const [processing, setProcessing] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState<CreateOfferRequest>({
    business_id: null,
    category_id: null,
    title: '',
    description: '',
    offer_type: 'percentage',
    discount_value: 0,
    min_purchase_amount: null,
    max_discount_amount: null,
    valid_from: '',
    valid_until: '',
    usage_limit: null,
    is_active: true,
  });

  useEffect(() => {
    fetchCategories();
    fetchBusinesses();
    fetchOffers();
  }, []);

  useEffect(() => {
    fetchOffers();
  }, [statusFilter, typeFilter, currentPage]);

  const fetchCategories = async () => {
    try {
      const data = await adminService.getAllCategories();
      setCategories(data.filter(c => c.is_active === 1));
    } catch (error: any) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchBusinesses = async () => {
    try {
      // Backend maximum limit is 100, so we fetch 100 businesses
      const data = await adminService.getAllBusinesses({ limit: 10000 });
      setBusinesses(data.businesses.filter(b => b.status === 'approved' || b.status === 'active'));
    } catch (error: any) {
      console.error('Failed to fetch businesses:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch businesses');
    }
  };

  const fetchOffers = async (showToast = false) => {
    try {
      if (showToast) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const params: GetOffersParams = {
        page: currentPage,
        limit: 20,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        offer_type: typeFilter !== 'all' ? typeFilter : undefined,
      };

      const data = await adminService.getAllOffers(params);
      setOffers(data.offers);
      setCurrentPage(data.pagination.current_page);
      setTotalPages(data.pagination.total_pages);
      setTotalOffers(data.pagination.total_offers);

      if (showToast) {
        toast.success('Offers refreshed!');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch offers');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setProcessing(true);
      await adminService.createOffer(formData);
      toast.success('Offer created successfully!');
      setCreateModal(false);
      resetForm();
      fetchOffers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create offer');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOffer) return;
    
    try {
      setProcessing(true);
      const updateData: UpdateOfferRequest = {
        offer_id: selectedOffer.offer_id,
        ...formData,
      };
      await adminService.updateOffer(updateData);
      toast.success('Offer updated successfully!');
      setEditModal(false);
      resetForm();
      fetchOffers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update offer');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedOffer) return;
    
    try {
      setProcessing(true);
      await adminService.deleteOffer(selectedOffer.offer_id);
      toast.success('Offer deleted successfully!');
      setDeleteModal(false);
      setSelectedOffer(null);
      fetchOffers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete offer');
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleStatus = async (offer: Offer) => {
    try {
      await adminService.toggleOfferStatus({
        offer_id: offer.offer_id,
        is_active: offer.is_active === 0,
      });
      toast.success(`Offer ${offer.is_active === 0 ? 'activated' : 'deactivated'} successfully!`);
      fetchOffers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to toggle offer status');
    }
  };

  const openEditModal = (offer: Offer) => {
    setSelectedOffer(offer);
    setFormData({
      business_id: offer.business_id,
      category_id: offer.category_id,
      title: offer.title,
      description: offer.description || '',
      offer_type: offer.offer_type,
      discount_value: parseFloat(offer.discount_value),
      min_purchase_amount: offer.min_purchase_amount ? parseFloat(offer.min_purchase_amount) : null,
      max_discount_amount: offer.max_discount_amount ? parseFloat(offer.max_discount_amount) : null,
      valid_from: offer.valid_from.split('T')[0],
      valid_until: offer.valid_until.split('T')[0],
      usage_limit: offer.usage_limit,
      is_active: offer.is_active === 1,
    });
    setEditModal(true);
  };

  const openDeleteModal = (offer: Offer) => {
    setSelectedOffer(offer);
    setDeleteModal(true);
  };

  const openViewModal = (offer: Offer) => {
    setSelectedOffer(offer);
    setViewModal(true);
  };

  const resetForm = () => {
    setFormData({
      business_id: null,
      category_id: null,
      title: '',
      description: '',
      offer_type: 'percentage',
      discount_value: 0,
      min_purchase_amount: null,
      max_discount_amount: null,
      valid_from: '',
      valid_until: '',
      usage_limit: null,
      is_active: true,
    });
    setSelectedOffer(null);
  };

  const getStatusBadge = (offer: Offer) => {
    const now = new Date();
    const validFrom = new Date(offer.valid_from);
    const validUntil = new Date(offer.valid_until);
    
    if (offer.is_active === 0) {
      return <span className="px-2 py-1 bg-secondary-200 text-secondary-700 rounded-full text-xs font-semibold">Inactive</span>;
    }
    if (now < validFrom) {
      return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">Scheduled</span>;
    }
    if (now > validUntil) {
      return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">Expired</span>;
    }
    return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Active</span>;
  };

  const filteredOffers = offers.filter(offer => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      offer.title.toLowerCase().includes(search) ||
      offer.description?.toLowerCase().includes(search) ||
      offer.business_name?.toLowerCase().includes(search) ||
      offer.category_name?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">Offers Management</h1>
          <p className="text-secondary-600">Create and manage promotional offers</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => fetchOffers(true)}
            disabled={refreshing}
            className="flex items-center space-x-2 px-4 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all disabled:opacity-50"
          >
            <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => navigate('/admin/offers/create')}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-all"
          >
            <Plus size={20} />
            <span>Create Offer</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-secondary-600 text-sm font-medium">Total Offers</p>
              <p className="text-2xl font-bold text-secondary-900 mt-1">{totalOffers}</p>
            </div>
            <Tag className="text-primary-500" size={32} />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-secondary-600 text-sm font-medium">Active Offers</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {offers.filter(o => o.is_active === 1 && new Date(o.valid_until) > new Date()).length}
              </p>
            </div>
            <CheckCircle className="text-green-500" size={32} />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-secondary-600 text-sm font-medium">Total Usage</p>
              <p className="text-2xl font-bold text-primary-600 mt-1">
                {offers.reduce((sum, o) => sum + o.total_usage, 0)}
              </p>
            </div>
            <TrendingUp className="text-primary-500" size={32} />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-secondary-600 text-sm font-medium">Expired Offers</p>
              <p className="text-2xl font-bold text-red-600 mt-1">
                {offers.filter(o => new Date(o.valid_until) < new Date()).length}
              </p>
            </div>
            <AlertCircle className="text-red-500" size={32} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" size={20} />
            <input
              type="text"
              placeholder="Search offers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as OfferStatus);
              setCurrentPage(1);
            }}
            className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="expired">Expired</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value as OfferType | 'all');
              setCurrentPage(1);
            }}
            className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
          >
            <option value="all">All Types</option>
            <option value="percentage">Percentage</option>
            <option value="fixed">Fixed</option>
            <option value="buy_one_get_one">Buy One Get One</option>
          </select>
          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
              setTypeFilter('all');
              setCurrentPage(1);
            }}
            className="px-4 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all"
          >
            <Filter size={20} className="inline mr-2" />
            Reset Filters
          </button>
        </div>
      </div>

      {/* Offers Table */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Title</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Type</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Discount</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Valid Period</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Usage</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Status</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-secondary-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-200">
              {filteredOffers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <AlertCircle className="mx-auto text-secondary-400 mb-4" size={48} />
                    <p className="text-secondary-600 font-medium">No offers found</p>
                    <p className="text-secondary-500 text-sm mt-2">Create your first offer to get started</p>
                  </td>
                </tr>
              ) : (
                filteredOffers.map((offer) => (
                  <tr key={offer.offer_id} className="hover:bg-secondary-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-semibold text-secondary-900">{offer.title}</p>
                        <p className="text-sm text-secondary-500 mt-1">
                          {offer.business_name && <><Store size={14} className="inline mr-1" />{offer.business_name}</>}
                          {offer.category_name && <><Package size={14} className="inline mr-1 ml-3" />{offer.category_name}</>}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold capitalize">
                        {offer.offer_type.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-1">
                        {offer.discount_type === 'percentage' ? (
                          <>
                            <Percent size={16} className="text-primary-500" />
                            <span className="font-semibold text-secondary-900">{offer.discount_value}%</span>
                          </>
                        ) : (
                          <>
                            <DollarSign size={16} className="text-primary-500" />
                            <span className="font-semibold text-secondary-900">${offer.discount_value}</span>
                          </>
                        )}
                      </div>
                      {offer.min_purchase_amount && (
                        <p className="text-xs text-secondary-500 mt-1">Min: ${offer.min_purchase_amount}</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="flex items-center space-x-1 text-secondary-600">
                          <Calendar size={14} />
                          <span>{new Date(offer.valid_from).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center space-x-1 text-secondary-600 mt-1">
                          <Clock size={14} />
                          <span>{new Date(offer.valid_until).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <p className="font-semibold text-secondary-900">{offer.total_usage}</p>
                        {offer.usage_limit && (
                          <p className="text-xs text-secondary-500">of {offer.usage_limit}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(offer)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => navigate(`/admin/offers/${offer.offer_id}`)}
                          className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-all"
                          title="View Details"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(offer)}
                          className="p-2 text-secondary-600 hover:bg-secondary-50 rounded-lg transition-all"
                          title={offer.is_active === 1 ? 'Deactivate' : 'Activate'}
                        >
                          {offer.is_active === 1 ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                        </button>
                        <button
                          onClick={() => navigate(`/admin/offers/${offer.offer_id}/edit`)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                          title="Edit"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => openDeleteModal(offer)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-secondary-200 flex items-center justify-between">
            <p className="text-sm text-secondary-600">
              Showing page {currentPage} of {totalPages} ({totalOffers} total offers)
            </p>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {createModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-secondary-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-secondary-900">Create New Offer</h2>
              <button
                onClick={() => {
                  setCreateModal(false);
                  resetForm();
                }}
                className="text-secondary-500 hover:text-secondary-700"
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    placeholder="Enter offer title"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    rows={3}
                    placeholder="Enter offer description"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Offer Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.offer_type}
                    onChange={(e) => setFormData({...formData, offer_type: e.target.value as OfferType})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                    <option value="buy_one_get_one">Buy One Get One</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Discount Value <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({...formData, discount_value: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    placeholder="Enter discount value"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Business (Optional)</label>
                  <select
                    value={formData.business_id || ''}
                    onChange={(e) => setFormData({...formData, business_id: e.target.value ? parseInt(e.target.value) : null})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  >
                    <option value="">Select Business (Optional)</option>
                    {businesses.map(b => (
                      <option key={b.business_id} value={b.business_id}>{b.business_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Category (Optional)</label>
                  <select
                    value={formData.category_id || ''}
                    onChange={(e) => setFormData({...formData, category_id: e.target.value ? parseInt(e.target.value) : null})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  >
                    <option value="">Select Category (Optional)</option>
                    {categories.map(c => (
                      <option key={c.category_id} value={c.category_id}>{c.category_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Min Purchase Amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.min_purchase_amount || ''}
                    onChange={(e) => setFormData({...formData, min_purchase_amount: e.target.value ? parseFloat(e.target.value) : null})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    placeholder="Enter minimum purchase amount"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Max Discount Amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.max_discount_amount || ''}
                    onChange={(e) => setFormData({...formData, max_discount_amount: e.target.value ? parseFloat(e.target.value) : null})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    placeholder="Enter maximum discount amount"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Valid From <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.valid_from}
                    onChange={(e) => setFormData({...formData, valid_from: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Valid Until <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.valid_until}
                    onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Usage Limit</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.usage_limit || ''}
                    onChange={(e) => setFormData({...formData, usage_limit: e.target.value ? parseInt(e.target.value) : null})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    placeholder="Enter usage limit (optional)"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      className="w-4 h-4 text-primary-500 border-secondary-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm font-semibold text-secondary-700">Active</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-secondary-200">
                <button
                  type="button"
                  onClick={() => {
                    setCreateModal(false);
                    resetForm();
                  }}
                  className="px-6 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="px-6 py-2 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-all disabled:opacity-50"
                >
                  {processing ? 'Creating...' : 'Create Offer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal - Similar to Create Modal */}
      {editModal && selectedOffer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-secondary-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-secondary-900">Edit Offer</h2>
              <button
                onClick={() => {
                  setEditModal(false);
                  resetForm();
                }}
                className="text-secondary-500 hover:text-secondary-700"
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleUpdate} className="p-6 space-y-4">
              {/* Same form fields as create modal */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Offer Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.offer_type}
                    onChange={(e) => setFormData({...formData, offer_type: e.target.value as OfferType})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                    <option value="buy_one_get_one">Buy One Get One</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Discount Value <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({...formData, discount_value: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Business (Optional)</label>
                  <select
                    value={formData.business_id || ''}
                    onChange={(e) => setFormData({...formData, business_id: e.target.value ? parseInt(e.target.value) : null})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  >
                    <option value="">Select Business (Optional)</option>
                    {businesses.map(b => (
                      <option key={b.business_id} value={b.business_id}>{b.business_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Category (Optional)</label>
                  <select
                    value={formData.category_id || ''}
                    onChange={(e) => setFormData({...formData, category_id: e.target.value ? parseInt(e.target.value) : null})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  >
                    <option value="">Select Category (Optional)</option>
                    {categories.map(c => (
                      <option key={c.category_id} value={c.category_id}>{c.category_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Min Purchase Amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.min_purchase_amount || ''}
                    onChange={(e) => setFormData({...formData, min_purchase_amount: e.target.value ? parseFloat(e.target.value) : null})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Max Discount Amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.max_discount_amount || ''}
                    onChange={(e) => setFormData({...formData, max_discount_amount: e.target.value ? parseFloat(e.target.value) : null})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Valid From <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.valid_from}
                    onChange={(e) => setFormData({...formData, valid_from: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Valid Until <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.valid_until}
                    onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Usage Limit</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.usage_limit || ''}
                    onChange={(e) => setFormData({...formData, usage_limit: e.target.value ? parseInt(e.target.value) : null})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      className="w-4 h-4 text-primary-500 border-secondary-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm font-semibold text-secondary-700">Active</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-secondary-200">
                <button
                  type="button"
                  onClick={() => {
                    setEditModal(false);
                    resetForm();
                  }}
                  className="px-6 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="px-6 py-2 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-all disabled:opacity-50"
                >
                  {processing ? 'Updating...' : 'Update Offer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal && selectedOffer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="text-red-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-secondary-900">Delete Offer</h2>
                  <p className="text-sm text-secondary-600">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-secondary-700 mb-6">
                Are you sure you want to delete the offer <strong>"{selectedOffer.title}"</strong>?
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setDeleteModal(false);
                    setSelectedOffer(null);
                  }}
                  className="px-6 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={processing}
                  className="px-6 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-all disabled:opacity-50"
                >
                  {processing ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewModal && selectedOffer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-secondary-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-secondary-900">Offer Details</h2>
              <button
                onClick={() => {
                  setViewModal(false);
                  setSelectedOffer(null);
                }}
                className="text-secondary-500 hover:text-secondary-700"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Title</p>
                  <p className="font-semibold text-secondary-900">{selectedOffer.title}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Status</p>
                  {getStatusBadge(selectedOffer)}
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Type</p>
                  <p className="font-semibold text-secondary-900 capitalize">{selectedOffer.offer_type.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Discount</p>
                  <p className="font-semibold text-secondary-900">
                    {selectedOffer.discount_type === 'percentage' 
                      ? `${selectedOffer.discount_value}%`
                      : `$${selectedOffer.discount_value}`}
                  </p>
                </div>
                {selectedOffer.description && (
                  <div className="col-span-2">
                    <p className="text-sm text-secondary-600 mb-1">Description</p>
                    <p className="text-secondary-900">{selectedOffer.description}</p>
                  </div>
                )}
                {selectedOffer.business_name && (
                  <div>
                    <p className="text-sm text-secondary-600 mb-1">Business</p>
                    <p className="font-semibold text-secondary-900">{selectedOffer.business_name}</p>
                  </div>
                )}
                {selectedOffer.category_name && (
                  <div>
                    <p className="text-sm text-secondary-600 mb-1">Category</p>
                    <p className="font-semibold text-secondary-900">{selectedOffer.category_name}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Valid From</p>
                  <p className="font-semibold text-secondary-900">{new Date(selectedOffer.valid_from).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Valid Until</p>
                  <p className="font-semibold text-secondary-900">{new Date(selectedOffer.valid_until).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Usage</p>
                  <p className="font-semibold text-secondary-900">
                    {selectedOffer.total_usage} {selectedOffer.usage_limit ? `of ${selectedOffer.usage_limit}` : ''}
                  </p>
                </div>
                {selectedOffer.min_purchase_amount && (
                  <div>
                    <p className="text-sm text-secondary-600 mb-1">Min Purchase</p>
                    <p className="font-semibold text-secondary-900">${selectedOffer.min_purchase_amount}</p>
                  </div>
                )}
                {selectedOffer.max_discount_amount && (
                  <div>
                    <p className="text-sm text-secondary-600 mb-1">Max Discount</p>
                    <p className="font-semibold text-secondary-900">${selectedOffer.max_discount_amount}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OffersManagement;

